import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { FieldGroup, Input, Notification } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { exportUsers } from '../../../api';
import { useAppSettings } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { linkToUnderline } from '../../../utils';
import { FormFooter, Notice } from '../../ui';
import { StyledSubtitle, StyledTitle } from './styles';

interface Props {
  projectId: string;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

const ExportUserModal = ({ onClose, onSuccess, projectId }: Props): JSX.Element => {
  const { signaturePath } = usePaths();
  const intl = useIntl();
  const { user } = useAppSettings();
  const { showErrorNotify } = useNotifications();
  const [fetching, setFetching] = useState(false);
  const [email, setEmail] = useState(user?.email || '');

  const handleSubmit = async () => {
    setFetching(true);

    try {
      await exportUsers(projectId, email);
      setFetching(false);
      onSuccess(email);
    } catch (e) {
      showErrorNotify(e);
      setFetching(false);
    }
  };

  return (
    <Modal>
      <StyledTitle center>{intl.formatMessage({ id: 'users.export.title' })}</StyledTitle>

      <FieldGroup>
        <StyledSubtitle>{intl.formatMessage({ id: 'users.export.subtitle' })}</StyledSubtitle>
        <StyledSubtitle>
          {intl.formatMessage({ id: 'users.export.subtitle-archive' })}
        </StyledSubtitle>
      </FieldGroup>

      <FieldGroup>
        <Notification icon={Notice}>
          {intl.formatMessage(
            { id: 'users.export.notification' },
            linkToUnderline({ href: signaturePath(projectId) })
          )}
        </Notification>
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({
          id: 'users.export.email'
        })}>
        <Input
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          errorMessage={!email ? intl.formatMessage({ id: 'app.validation.required' }) : undefined}
        />
      </FieldGroup>

      <FormFooter
        onSubmit={handleSubmit}
        onCancel={onClose}
        fetching={fetching}
        error={false}
        disabled={!email}
        isModal
        submitText={intl.formatMessage({ id: 'users.export.button' })}
      />
    </Modal>
  );
};

export default ExportUserModal;
